import { useState, useEffect, useCallback, useMemo } from "react";
import moment from "moment";
import useGetApiRequests from "../../services/axios/useApiRequests";
import { useSelector } from "react-redux";
import { triggerNotification } from "../../common/CommonExport";
import { AxiosResponse } from "axios";
import { RosterItem, ApiError, UserIdState, RosterApiResponse, EmployeeListApiResponse, RosterPlanItem, BulkRosterApiResponse, DeleteRosterPlanApiResponse } from "./types";
import { OrganisationIdState } from "../announcement/layouts/mailList/mailListsInterface";

export const useRosterController = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dataSource, setDataSource] = useState<RosterItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [viewMode, setViewMode] = useState<"plan" | "add">("plan");
  const [planMonth, setPlanMonth] = useState(moment().format("MM-YYYY"));
  const [addMonth, setAddMonth] = useState(moment().format("MM-YYYY"));
  const [editingKey, setEditingKey] = useState<string | number>("");
  const [originalRecord, setOriginalRecord] = useState<RosterItem | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const orgId = useSelector((state: OrganisationIdState) => state?.organisationId?.id);
  const reportingManagerId = useSelector((state: UserIdState) => state?.userId?.id);

  const getEmployeeRosterList = useGetApiRequests("employeeRosterList", "GET");
  const getRosterPlan = useGetApiRequests("getRosterPlan", "GET");
  const updateRosterPlan = useGetApiRequests("updateEmployeeRosterPlan", "PUT");
  const bulkRosterUpload = useGetApiRequests("bulkApiUpload", "POST");
  const deleteRosterPlan = useGetApiRequests("deleteEmployeeRosterPlan", "DELETE");

  const memoizedGetEmployeeRosterList = useMemo(() => getEmployeeRosterList, []);
  const memoizedUpdateRosterPlan = useMemo(() => updateRosterPlan, []);
  const memoizedGetRosterPlan = useMemo(() => getRosterPlan, []);
  const memoizedBulkRosterUpload = useMemo(() => bulkRosterUpload, []);
  const memoizedDeleteRosterPlan = useMemo(() => deleteRosterPlan, []);

  const fetchRosterData = useCallback(
    async (page: number, size: number, mode: "plan" | "add") => {
      if (!orgId || !reportingManagerId) {
        return;
      }
      setLoading(true);

      const currentMonth = mode === "plan" ? planMonth : addMonth;

      try {
        let response: RosterApiResponse | EmployeeListApiResponse;
        if (mode === "plan") {
          response = (await memoizedGetRosterPlan(
            null,
            {
              organisationId: orgId,
              reportingMangerId: reportingManagerId,
              start: (page - 1) * size,
              limit: size,
            },
            {
              monthYear: currentMonth,
            },
          )) as RosterApiResponse;

          const planList = response?.data?.data || [];
          const apiData = (planList as RosterPlanItem[]).map((item: RosterPlanItem) => ({
            employeeRosterPlanId: item.employeeRosterPlanId,
            employeeId: item.employee.employeeId,
            firstName: item.employee.firstName,
            lastName: item.employee.lastName,
            code: item.employee.code,
            mon: item.monday,
            tue: item.tuesday,
            wed: item.wednesday,
            thu: item.thursday,
            fri: item.friday,
            sat: item.saturday,
            sun: item.sunday,
            key: item.employeeRosterPlanId,
          }));
          setDataSource(apiData);
          setTotalResults(response?.data?.totalResults || 0);
        } else {
          response = (await memoizedGetEmployeeRosterList(
            null,
            {
              organisationId: orgId,
              start: (page - 1) * size,
              limit: size,
              monthYear: currentMonth,
            },
            {
              reportingManagerId: reportingManagerId,
            },
          )) as EmployeeListApiResponse;

          const rosterList = response?.data?.data || [];
          const apiData = (rosterList as RosterItem[]).map((item: RosterItem) => ({
            ...item,
            mon: "",
            tue: "",
            wed: "",
            thu: "",
            fri: "",
            sat: "",
            sun: "",
            key: item.employeeId,
          }));
          setDataSource(apiData);
          setTotalResults(response?.data?.totalResults || 0);
        }
      } catch (error: unknown) {
        const apiError = error as ApiError;
        const errorMessage = apiError?.response?.data?.message || apiError?.message || "Failed to fetch roster data";
        triggerNotification("error", "", errorMessage, "topRight");
      } finally {
        setLoading(false);
      }
    },
    [orgId, reportingManagerId, memoizedGetRosterPlan, memoizedGetEmployeeRosterList, planMonth, addMonth],
  );

  const handleCellChange = (value: string, record: RosterItem, day: string) => {
    setDataSource((prev) =>
      prev.map((item) => {
        if (item.employeeId === record.employeeId) {
          return { ...item, [day]: value };
        }
        return item;
      }),
    );
  };

  const handleBulkAdd = async () => {
    if (!orgId || !reportingManagerId) {
      triggerNotification("error", "Error", "Missing Organisation or Manager ID", "topRight");
      return;
    }

    const payload = dataSource
      .filter((item) => item.sun || item.mon || item.tue || item.wed || item.thu || item.fri || item.sat)
      .map((item) => {
        const rosterItem: Partial<RosterPlanItem> & { monthYear: string } = {
          monthYear: moment(addMonth, "MM-YYYY").format("MM-YYYY"),
          employee: {
            employeeId: item.employeeId,
            firstName: item.firstName,
            lastName: item.lastName,
            code: item.code,
          },
        };
        if (item.sun) rosterItem.sunday = item.sun;
        if (item.mon) rosterItem.monday = item.mon;
        if (item.tue) rosterItem.tuesday = item.tue;
        if (item.wed) rosterItem.wednesday = item.wed;
        if (item.thu) rosterItem.thursday = item.thu;
        if (item.fri) rosterItem.friday = item.fri;
        if (item.sat) rosterItem.saturday = item.sat;
        return rosterItem;
      });

    if (payload.length === 0) {
      triggerNotification("warning", "Warning", "No changes to upload", "topRight");
      return;
    }

    setLoading(true);
    try {
      const response = await memoizedBulkRosterUpload(payload, { organisationId: orgId }, { id: reportingManagerId });
      const res = response as BulkRosterApiResponse;
      if (res?.status === 200 || res?.status === 201) {
        triggerNotification("success", "", res?.data?.message || "Roster Plan Added Successfully", "topRight");

        fetchRosterData(currentPage, pageSize, viewMode);
      } else {
        triggerNotification("error", "Error", res?.data?.message || "Failed to add roster plan", "topRight");
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      const errorMessage = apiError?.response?.data?.message || apiError?.message || "Failed to add roster plan";
      triggerNotification("error", "Error", errorMessage, "topRight");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRoster = async (record: RosterItem) => {
    const currentRecord = dataSource.find((item) => item.employeeId === record.employeeId) || record;

    if (viewMode === "add") {
      if (!currentRecord.sun || !currentRecord.mon || !currentRecord.tue || !currentRecord.wed || !currentRecord.thu || !currentRecord.fri || !currentRecord.sat) {
        triggerNotification("error", "", "Please select a shift for all days", "topRight");
        return;
      }
      setEditingKey("");
      setOriginalRecord(null);
      return;
    }

    if (!reportingManagerId) {
      triggerNotification("error", "Error", "Reporting Manager ID is missing", "topRight");
      return;
    }

    if (!currentRecord.employeeRosterPlanId) {
      triggerNotification("error", "Error", "Roster Plan ID is missing", "topRight");
      return;
    }

    const payload = {
      employeeRosterPlanId: currentRecord.employeeRosterPlanId,
      sunday: currentRecord.sun,
      monday: currentRecord.mon,
      tuesday: currentRecord.tue,
      wednesday: currentRecord.wed,
      thursday: currentRecord.thu,
      friday: currentRecord.fri,
      saturday: currentRecord.sat,
      employee: {
        employeeId: currentRecord.employeeId,
      },
    };

    setLoading(true);
    try {
      const response = await memoizedUpdateRosterPlan(payload, {}, { id: reportingManagerId }, {});
      const res = response as BulkRosterApiResponse;
      if (response?.status === 200) {
        triggerNotification("success", "", res?.data?.message || "Employee Roster Plan edited Successfully", "topRight");
        fetchRosterData(currentPage, pageSize, viewMode);
        setEditingKey("");
      } else {
        triggerNotification("error", "Error", "Failed to update roster plan", "topRight");
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      const errorMessage = apiError?.response?.data?.message || apiError?.message || "Failed to update roster plan";
      triggerNotification("error", "Error", errorMessage, "topRight");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record: RosterItem) => {
    setOriginalRecord({ ...record });
    setEditingKey(record.key || "");
  };

  const handleCancel = () => {
    if (originalRecord) {
      setDataSource((prev) => prev.map((item) => (item.employeeId === originalRecord.employeeId ? originalRecord : item)));
    }
    setEditingKey("");
    setOriginalRecord(null);
  };

  const onShowSizeChange = (current: number, newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const isBulkAddDisabled = useMemo(() => {
    if (viewMode !== "add") return false;
    return loading || editingKey !== "" || !dataSource.some((item) => item.sun || item.mon || item.tue || item.wed || item.thu || item.fri || item.sat);
  }, [dataSource, viewMode, loading, editingKey]);

  const handleDelete = async (ids: (string | number)[]) => {
    if (!reportingManagerId) {
      triggerNotification("error", "Error", "Reporting Manager ID is missing", "topRight");
      return;
    }

    setLoading(true);
    try {
      const response = await memoizedDeleteRosterPlan(ids, {}, { id: reportingManagerId });
      const res = (response as AxiosResponse<DeleteRosterPlanApiResponse>).data;
      if (res?.status?.success === "Success") {
        triggerNotification("success", "", res?.status?.message || "Roster Plan(s) deleted successfully", "topRight");
        fetchRosterData(currentPage, pageSize, viewMode);
        setSelectedRowKeys([]);
      } else {
        triggerNotification("error", "Error", "Failed to delete roster plan", "topRight");
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      const errorMessage = apiError?.response?.status?.message || apiError?.message || "Failed to delete roster plan";
      triggerNotification("error", "Error", errorMessage, "topRight");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRosterData(currentPage, pageSize, viewMode);
  }, [currentPage, pageSize, viewMode, fetchRosterData]);

  return {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    dataSource,
    setDataSource,
    loading,
    totalResults,
    viewMode,
    setViewMode,
    planMonth,
    setPlanMonth,
    addMonth,
    setAddMonth,
    editingKey,
    setEditingKey,
    originalRecord,
    setOriginalRecord,
    handleCellChange,
    handleBulkAdd,
    handleSaveRoster,
    handleEdit,
    handleCancel,
    onShowSizeChange,
    isBulkAddDisabled,
    fetchRosterData,
    selectedRowKeys,
    setSelectedRowKeys,
    handleDelete,
  };
};
